import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  CloudArrowDown,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  EnvelopeSimple,
  FloppyDisk,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

export default function BackupPage() {
  const { user } = useAuth();
  const canRun = user?.role === 'admin' || user?.role === 'owner';

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/backup/status');
      setStatus(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load backup status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runBackup = async () => {
    if (!canRun) return;
    if (!window.confirm('Abhi backup chalaye? Email bhi send hoga configured recipients ko.')) return;
    setRunning(true);
    try {
      const res = await api.post('/backup/run-now');
      if (res.data.success) {
        toast.success(
          `Backup done — ${res.data.document_count} docs, email ${res.data.email_sent ? 'sent ✓' : 'skipped'}`
        );
      } else {
        toast.error(`Backup failed: ${res.data.error || 'Unknown error'}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const downloadFile = (fileName) => {
    const url = `${API_BASE}/api/backup/download/${fileName}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fmtBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading backup status...</div>;
  }

  const last = status?.last_run;
  const files = status?.local_files || [];
  const recipients = status?.recipients || [];

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <FloppyDisk size={28} weight="duotone" className="text-emerald-600" />
          Data Backup & Retention
        </h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          Automatic daily backup 2:00 AM IST ko chalta hai. Har backup compressed file email pe bhi
          jaati hai — off-site permanent copy. Manual run + download bhi available hai.
        </p>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Last run */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Last Backup</div>
          {last ? (
            <>
              <div className="flex items-center gap-2">
                {last.success ? (
                  <CheckCircle size={22} weight="fill" className="text-emerald-500" />
                ) : (
                  <XCircle size={22} weight="fill" className="text-red-500" />
                )}
                <div className="text-lg font-semibold text-gray-900">
                  {last.success ? 'Success' : 'Failed'}
                </div>
              </div>
              <div className="text-sm text-gray-600 mt-2" data-testid="last-backup-time">
                {fmtDate(last.started_at)}
              </div>
              {last.success && (
                <div className="text-xs text-gray-500 mt-1">
                  {last.document_count} docs • {fmtBytes(last.file_size_bytes)}
                </div>
              )}
              {last.error && (
                <div className="text-xs text-red-600 mt-2 break-words">{last.error}</div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-gray-500">
              <Clock size={22} />
              <span className="text-sm">No backup yet</span>
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Schedule</div>
          <div className="flex items-center gap-2">
            <Clock size={22} weight="duotone" className="text-indigo-500" />
            <div className="text-lg font-semibold text-gray-900">Daily 2:00 AM IST</div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Retention: last {status?.retention_count || 14} backups kept locally
          </div>
        </div>

        {/* Email recipients */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Email Recipients</div>
          <div className="flex items-start gap-2">
            <EnvelopeSimple size={22} weight="duotone" className="text-amber-600 mt-0.5" />
            <div className="space-y-0.5">
              {recipients.length === 0 && (
                <div className="text-xs text-gray-500">None configured</div>
              )}
              {recipients.map((e) => (
                <div key={e} className="text-sm font-medium text-gray-800 break-all">
                  {e}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Manual trigger */}
      {canRun && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-semibold text-emerald-900">Run Backup Now</div>
            <div className="text-xs text-emerald-800 mt-0.5">
              Ek compressed DB dump banega + email pe attached bhej dega.
            </div>
          </div>
          <Button
            onClick={runBackup}
            disabled={running}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="run-backup-btn"
          >
            <CloudArrowDown size={18} weight="bold" />
            {running ? 'Running...' : 'Backup Now'}
          </Button>
        </div>
      )}

      {/* Local files list */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="font-semibold text-gray-900">Local Backups</div>
          <div className="text-xs text-gray-500">{files.length} files</div>
        </div>
        {files.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            <FloppyDisk size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
            Abhi koi backup file nahi hai. Upar "Backup Now" button se pehla backup bana sakte ho.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 text-left">File</th>
                  <th className="px-4 py-2.5 text-left">Size</th>
                  <th className="px-4 py-2.5 text-left">Created</th>
                  <th className="px-4 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr
                    key={f.file_name}
                    className="border-t border-gray-100 hover:bg-emerald-50/40"
                    data-testid={`backup-file-row-${f.file_name}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-800 break-all">
                      {f.file_name}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {fmtBytes(f.size_bytes)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {fmtDate(f.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadFile(f.file_name)}
                        data-testid={`download-backup-${f.file_name}`}
                        className="h-8 gap-1.5"
                      >
                        <Download size={14} weight="bold" />
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
